import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../supabaseClient';

/**
 * TDALeaderForm — Leader questionnaire for Team Dynamics Alignment.
 * After creating a team in TDATeamAssembly, the leader provides context
 * about the team's purpose, goals, challenges, and member roles.
 *
 * Route: /results/tda/questionnaire/:teamId
 *
 * Requires: user is logged in, user is the leader of this team.
 *
 * Fields:
 *   - Team purpose (required) — what does this team do?
 *   - Team goals (optional) — what is the team working toward?
 *   - Team context/challenges (optional) — any communication challenges?
 *   - Member roles (optional per member) — role context for each member
 *
 * Saves to tda_teams record (purpose, goals, context columns) and
 * updates member roles in tda_team_members via save-tda-input.js.
 *
 * If minimal input provided, the report will default to pure PASS data analysis.
 */
function TDALeaderForm() {
  const { teamId } = useParams();
  const { user, profile, loading: authLoading, signOut } = useAuth();

  // Team data
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamError, setTeamError] = useState('');

  // Form fields
  const [teamPurpose, setTeamPurpose] = useState('');
  const [teamGoals, setTeamGoals] = useState('');
  const [teamContext, setTeamContext] = useState('');
  const [memberRoles, setMemberRoles] = useState({});

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Redirect if not purchased
  useEffect(() => {
    if (!authLoading && profile && profile.tda_purchased !== true) {
      window.location.href = '/results';
    }
  }, [authLoading, profile]);

  // Fetch team + members data
  useEffect(() => {
    if (!user || authLoading || !teamId) return;

    const fetchTeamData = async () => {
      setTeamLoading(true);
      setTeamError('');

      // Fetch team
      const { data: teamData, error: teamFetchError } = await supabase
        .from('tda_teams')
        .select('id, team_name, leader_user_id, report_status, team_purpose, team_goals, team_context')
        .eq('id', teamId)
        .single();

      if (teamFetchError || !teamData) {
        setTeamError('Team not found. Please go back to your portal and try again.');
        setTeamLoading(false);
        return;
      }

      // Verify this user is the leader
      if (teamData.leader_user_id !== user.id) {
        setTeamError('You are not the leader of this team.');
        setTeamLoading(false);
        return;
      }

      // Don't allow editing if report is already generating or completed
      if (teamData.report_status === 'generating' || teamData.report_status === 'completed') {
        setTeamError('This team already has a report in progress or completed.');
        setTeamLoading(false);
        return;
      }

      setTeam(teamData);

      // Pre-fill if questionnaire was previously started
      if (teamData.team_purpose) setTeamPurpose(teamData.team_purpose);
      if (teamData.team_goals) setTeamGoals(teamData.team_goals);
      if (teamData.team_context) setTeamContext(teamData.team_context);

      // Fetch members
      const { data: memberData, error: memberFetchError } = await supabase
        .from('tda_team_members')
        .select('id, email, member_name, role_on_team')
        .eq('team_id', teamId)
        .order('created_at', { ascending: true });

      if (!memberFetchError && memberData) {
        setMembers(memberData);
        // Pre-fill member roles
        const roles = {};
        memberData.forEach(m => {
          roles[m.id] = m.role_on_team || '';
        });
        setMemberRoles(roles);
      }

      setTeamLoading(false);
    };

    fetchTeamData();
  }, [user, authLoading, teamId]);

  // Update a member's role
  const updateMemberRole = (memberId, role) => {
    setMemberRoles(prev => ({ ...prev, [memberId]: role }));
  };

  // Submit questionnaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    if (!teamPurpose.trim()) {
      setError('Please describe your team\'s purpose.');
      setSubmitting(false);
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('You are not logged in. Please sign in again.');
        setSubmitting(false);
        return;
      }

      // Build member_roles array for roles that were filled in
      const memberRolesArray = members
        .filter(m => memberRoles[m.id] !== undefined)
        .map(m => ({
          member_id: m.id,
          role_on_team: memberRoles[m.id].trim() || null,
        }));

      const response = await fetch('/.netlify/functions/save-tda-input', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          team_id: teamId,
          team_purpose: teamPurpose.trim(),
          team_goals: teamGoals.trim() || null,
          team_context: teamContext.trim() || null,
          member_roles: memberRolesArray,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to save your responses. Please try again.');
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
      // Redirect to portal after brief confirmation
      setTimeout(() => {
        window.location.href = '/results';
      }, 3000);
    } catch (err) {
      console.error('TDALeaderForm submit error:', err);
      setError('Network error: ' + (err.message || 'Please check your connection and try again.'));
      setSubmitting(false);
    }
  };

  if (authLoading || teamLoading) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  // Error state — team not found or not authorized
  if (teamError) {
    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <div style={styles.headerInner}>
            <img src="/pass-logo.png" alt="PASS Profile 3.0" style={styles.logo} />
          </div>
        </header>
        <main style={styles.main}>
          <div style={styles.errorCard}>
            <h2 style={styles.errorCardTitle}>Unable to Load Team</h2>
            <p style={styles.errorCardText}>{teamError}</p>
            <a href="/results" style={styles.backLink}>Back to Portal</a>
          </div>
        </main>
      </div>
    );
  }

  // Success state after submission
  if (submitted) {
    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <div style={styles.headerInner}>
            <img src="/pass-logo.png" alt="PASS Profile 3.0" style={styles.logo} />
          </div>
        </header>
        <main style={styles.main}>
          <div style={styles.successCard}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#10003;</div>
            <h2 style={styles.successTitle}>Team Context Saved!</h2>
            <p style={styles.successText}>
              Your team context for "{team?.team_name}" has been saved.
              Your Team Dynamics Alignment report will be available once report
              generation is fully set up (coming soon).
            </p>
            <p style={styles.successSub}>Redirecting to your portal...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <img src="/pass-logo.png" alt="PASS Profile 3.0" style={styles.logo} />
          <button onClick={signOut} style={styles.signOutBtn}>Sign Out</button>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.container}>
          {/* Intro banner */}
          <div style={styles.introBanner}>
            <h1 style={styles.introTitle}>Team Context</h1>
            <p style={styles.introSubtitle}>
              Tell us about your team so we can personalize your Team Dynamics
              Alignment report. The more context you provide, the more specific
              the insights will be.
            </p>
            {team && (
              <div style={styles.teamNameBadge}>
                {team.team_name}
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={styles.form}>
            {error && <div style={styles.errorBox}>{error}</div>}

            {/* Team purpose — required */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                What does this team do? <span style={styles.required}>*</span>
              </label>
              <p style={styles.fieldHint}>
                Describe the team's main purpose or mission in 1-3 sentences.
                This is the foundation of your report.
              </p>
              <textarea
                value={teamPurpose}
                onChange={(e) => setTeamPurpose(e.target.value)}
                required
                disabled={submitting}
                placeholder={'e.g., "We lead the company\'s product strategy and manage a cross-functional team of designers and engineers."'}
                style={styles.textarea}
                rows={3}
                maxLength={2000}
              />
            </div>

            {/* Team goals — optional */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                What is the team working toward right now?
              </label>
              <p style={styles.fieldHint}>
                Current goals, projects, or milestones. This helps us tailor the
                communication guidance to what matters most. (Optional)
              </p>
              <textarea
                value={teamGoals}
                onChange={(e) => setTeamGoals(e.target.value)}
                disabled={submitting}
                placeholder='e.g., "Launching a new product line by Q3, improving cross-team collaboration, onboarding three new team members."'
                style={styles.textarea}
                rows={3}
                maxLength={2000}
              />
            </div>

            {/* Team context/challenges — optional */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Any communication dynamics you'd like us to focus on?
              </label>
              <p style={styles.fieldHint}>
                Is there anything specific happening on the team, such as a transition,
                new members, a challenge, or something you want to strengthen?
                This helps us focus the analysis. (Optional)
              </p>
              <textarea
                value={teamContext}
                onChange={(e) => setTeamContext(e.target.value)}
                disabled={submitting}
                placeholder='e.g., "We recently merged two teams and are working on building trust. Some members are remote, others in-office."'
                style={styles.textarea}
                rows={3}
                maxLength={2000}
              />
            </div>

            {/* Member roles — optional per member */}
            {members.length > 0 && (
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Team Member Roles</label>
                <p style={styles.fieldHint}>
                  Describe each member's role or function on this team. This helps
                  the report show how each person's communication style fits their
                  team responsibilities. (Optional)
                </p>
                <div style={styles.memberRolesSection}>
                  {members.map((member) => (
                    <div key={member.id} style={styles.memberRoleRow}>
                      <div style={styles.memberInfo}>
                        <span style={styles.memberNameLabel}>
                          {member.member_name || member.email}
                        </span>
                        {member.member_name && (
                          <span style={styles.memberEmailLabel}>{member.email}</span>
                        )}
                      </div>
                      <input
                        type="text"
                        value={memberRoles[member.id] || ''}
                        onChange={(e) => updateMemberRole(member.id, e.target.value)}
                        disabled={submitting}
                        placeholder="Role on team (optional)"
                        style={styles.memberRoleInput}
                        maxLength={200}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Minimal input note */}
            <div style={styles.minimalNote}>
              <p style={styles.minimalNoteText}>
                Only the team purpose is required. If you skip the optional fields,
                your report will focus on what the PASS data reveals about your
                team's communication patterns.
              </p>
            </div>

            {/* Form actions */}
            <div style={styles.formActions}>
              <a href="/results" style={styles.cancelLink}>Back to Portal</a>
              <button
                type="submit"
                disabled={submitting || !teamPurpose.trim()}
                style={{
                  ...styles.submitBtn,
                  ...((submitting || !teamPurpose.trim()) ? styles.submitBtnDisabled : {}),
                }}
              >
                {submitting ? 'Saving...' : 'Save Team Context'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

/* ===== STYLES ===== */
const styles = {
  page: {
    minHeight: '100vh',
    background: '#F8F9FA',
    fontFamily: 'Inter, system-ui, sans-serif',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#F8F9FA',
  },
  loadingText: {
    color: '#6B7A8F',
    fontSize: 16,
  },
  header: {
    backgroundColor: '#FFFFFF',
    borderBottom: '3px solid #2A9D8F',
    padding: '16px 24px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  headerInner: {
    maxWidth: 1100,
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    height: '40px',
  },
  signOutBtn: {
    background: 'transparent',
    border: '1px solid #0B3D2E',
    color: '#0B3D2E',
    padding: '8px 16px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
  },
  main: {
    flex: 1,
    padding: '32px 0',
  },
  container: {
    maxWidth: 700,
    margin: '0 auto',
    padding: '0 24px',
  },

  // Intro banner
  introBanner: {
    background: 'linear-gradient(135deg, #0B3D2E 0%, #2A9D8F 100%)',
    borderRadius: 12,
    padding: '32px 40px',
    marginBottom: 32,
    textAlign: 'center',
  },
  introTitle: {
    color: 'white',
    fontSize: 28,
    fontWeight: 700,
    margin: '0 0 12px 0',
    fontFamily: 'Outfit, Inter, sans-serif',
  },
  introSubtitle: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 15,
    lineHeight: 1.6,
    margin: 0,
  },
  teamNameBadge: {
    display: 'inline-block',
    background: 'rgba(255,255,255,0.2)',
    color: 'white',
    padding: '6px 16px',
    borderRadius: 20,
    fontSize: 14,
    fontWeight: 600,
    marginTop: 16,
  },

  // Form
  form: {
    background: 'white',
    borderRadius: 12,
    padding: '32px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #E8ECF0',
  },
  errorBox: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 8,
    padding: '12px 16px',
    color: '#DC2626',
    fontSize: 14,
    marginBottom: 24,
  },
  fieldGroup: {
    marginBottom: 24,
  },
  label: {
    display: 'block',
    fontSize: 15,
    fontWeight: 600,
    color: '#0B3D2E',
    marginBottom: 6,
  },
  required: {
    color: '#DC2626',
  },
  fieldHint: {
    fontSize: 13,
    color: '#6B7A8F',
    margin: '0 0 8px 0',
    lineHeight: 1.4,
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 15,
    border: '1px solid #D1D5DB',
    borderRadius: 8,
    fontFamily: 'Inter, system-ui, sans-serif',
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
  },

  // Member roles section
  memberRolesSection: {
    border: '1px solid #E8ECF0',
    borderRadius: 8,
    overflow: 'hidden',
  },
  memberRoleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    borderBottom: '1px solid #F0F0F0',
  },
  memberInfo: {
    flex: '0 0 200px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  memberNameLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  memberEmailLabel: {
    fontSize: 12,
    color: '#6B7A8F',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  memberRoleInput: {
    flex: 1,
    padding: '8px 12px',
    fontSize: 14,
    border: '1px solid #E0E0E0',
    borderRadius: 6,
    fontFamily: 'Inter, system-ui, sans-serif',
    outline: 'none',
    boxSizing: 'border-box',
    color: '#333',
  },

  // Minimal input note
  minimalNote: {
    background: '#F0FAF4',
    border: '1px solid #C8E6D0',
    borderRadius: 8,
    padding: '12px 16px',
    marginBottom: 24,
  },
  minimalNoteText: {
    fontSize: 13,
    color: '#0B3D2E',
    margin: 0,
    lineHeight: 1.5,
  },

  // Form actions
  formActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 32,
    paddingTop: 24,
    borderTop: '1px solid #E8ECF0',
  },
  cancelLink: {
    color: '#6B7A8F',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
  },
  submitBtn: {
    background: '#2A9D8F',
    color: 'white',
    padding: '14px 28px',
    borderRadius: 8,
    border: 'none',
    fontWeight: 600,
    fontSize: 15,
    cursor: 'pointer',
    transition: 'background 0.2s',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  submitBtnDisabled: {
    background: '#9CA3AF',
    cursor: 'not-allowed',
  },

  // Error card
  errorCard: {
    background: 'white',
    borderRadius: 12,
    padding: '48px 40px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #E8ECF0',
    textAlign: 'center',
    maxWidth: 600,
    margin: '64px auto',
  },
  errorCardTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#0B3D2E',
    margin: '0 0 12px 0',
  },
  errorCardText: {
    fontSize: 15,
    color: '#6B7A8F',
    lineHeight: 1.6,
    margin: '0 0 24px 0',
  },
  backLink: {
    display: 'inline-block',
    background: '#2A9D8F',
    color: 'white',
    padding: '12px 24px',
    borderRadius: 8,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 14,
  },

  // Success state
  successCard: {
    background: 'white',
    borderRadius: 12,
    padding: '48px 40px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #E8ECF0',
    textAlign: 'center',
    maxWidth: 600,
    margin: '64px auto',
  },
  successTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#0B3D2E',
    margin: '0 0 12px 0',
  },
  successText: {
    fontSize: 15,
    color: '#6B7A8F',
    lineHeight: 1.6,
    margin: '0 0 16px 0',
  },
  successSub: {
    fontSize: 13,
    color: '#6B7A8F',
    fontStyle: 'italic',
  },
};

export default TDALeaderForm;
